import {
  aliasedTable,
  and,
  asc,
  category,
  desc,
  eq,
  gte,
  isNotNull,
  like,
  lt,
  lte,
  or,
  type PowerSyncDrizzleDB,
  sql,
  tx,
  wallet,
  wallet_group,
} from "@cashsync/powersync";
import { liveCategoryFilters, liveTxFilters, liveWalletFilters } from "../live/filters";
import { expenseContributionSql, incomeContributionSql, walletDeltaSql } from "./money-sql";

/**
 * Financial domain query builders. All operate against local PowerSync
 * SQLite — zero network. Amounts are integers in minor units.
 */

// ─── Cuentas (wallets) ────────────────────────────────────────────────────────

export function getWalletsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: wallet.id,
      name: wallet.name,
      kind: wallet.kind,
      currency: wallet.currency,
      initial_balance: wallet.initial_balance,
      icon: wallet.icon,
      color: wallet.color,
      sort_order: wallet.sort_order,
      group_id: wallet.group_id,
      group_name: wallet_group.name,
      archived: wallet.archived,
      exclude_from_totals: wallet.exclude_from_totals,
    })
    .from(wallet)
    .leftJoin(wallet_group, eq(wallet.group_id, wallet_group.id))
    .where(and(eq(wallet.user_id, userId), ...liveWalletFilters()))
    .orderBy(asc(wallet.sort_order), asc(wallet.created_at));
}

export type WalletWithMeta = Awaited<
  ReturnType<ReturnType<typeof getWalletsQuery>["execute"]>
>[number];

/**
 * Derived LIVE balance per account (decision D4): initial_balance + aggregation
 * of non-deleted transactions. Single grouped pass; contribution rule
 * lives in `walletDeltaSql` (single choke point shared with reconciliation).
 */
export function getWalletBalancesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      wallet_id: wallet.id,
      balance:
        sql<number>`${wallet.initial_balance} + COALESCE(SUM(${walletDeltaSql(sql`${wallet.id}`)}), 0)`.as(
          "balance",
        ),
    })
    .from(wallet)
    .leftJoin(
      tx,
      and(or(eq(tx.wallet_id, wallet.id), eq(tx.to_wallet_id, wallet.id)), ...liveTxFilters()),
    )
    .where(and(eq(wallet.user_id, userId), ...liveWalletFilters()))
    .groupBy(wallet.id);
}

export interface WalletBalanceRow {
  wallet_id: string;
  balance: number;
}

/**
 * Reconciliation of ONE account (D4 + verified): full derived balance vs.
 * reconciled balance (only contribution of txs marked `cleared`). The difference
 * (balance − cleared_balance) is what remains to match against statement; when
 * it reaches 0 the account is reconciled.
 *
 * Contribution of each tx follows exactly `getWalletBalancesQuery` rules
 * (income +, expense −, outgoing transfer −(amount+fee), incoming +). The
 * `initial_balance` counts in both total and reconciled balance: it is the
 * verified starting point of the account.
 */
export function getReconciliationSummaryQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  walletId: string,
) {
  const contribution = (clearedOnly: boolean) =>
    sql<number>`COALESCE(SUM(${walletDeltaSql(sql`${walletId}`, { clearedOnly })}), 0)`;
  return db
    .select({
      wallet_id: wallet.id,
      balance: sql<number>`${wallet.initial_balance} + ${contribution(false)}`.as("balance"),
      cleared_balance: sql<number>`${wallet.initial_balance} + ${contribution(true)}`.as(
        "cleared_balance",
      ),
    })
    .from(wallet)
    .leftJoin(
      tx,
      and(or(eq(tx.wallet_id, wallet.id), eq(tx.to_wallet_id, wallet.id)), ...liveTxFilters()),
    )
    .where(and(eq(wallet.id, walletId), eq(wallet.user_id, userId)))
    .groupBy(wallet.id);
}

export interface ReconciliationSummaryRow {
  wallet_id: string;
  /** Full derived balance (all non-deleted txs). */
  balance: number;
  /** Saldo conciliado (solo tx verificadas). */
  cleared_balance: number;
}

export function getWalletGroupsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(wallet_group)
    .where(eq(wallet_group.user_id, userId))
    .orderBy(asc(wallet_group.sort_order));
}

// ─── Categories ───────────────────────────────────────────────────────────────

export function getCategoriesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: category.id,
      parent_id: category.parent_id,
      kind: category.kind,
      name: category.name,
      name_key: category.name_key,
      icon: category.icon,
      color: category.color,
      sort_order: category.sort_order,
    })
    .from(category)
    .where(and(eq(category.user_id, userId), ...liveCategoryFilters()))
    .orderBy(asc(category.sort_order), asc(category.name));
}

export interface CategoryRow {
  id: string;
  parent_id: string | null;
  kind: string | null;
  name: string | null;
  name_key: string | null;
  icon: string | null;
  color: string | null;
  sort_order: number | null;
}

export interface CategoryNode extends CategoryRow {
  children: CategoryRow[];
}

/** Builds 2-level in-memory tree from flat list. */
export function buildCategoryTree(
  rows: CategoryRow[],
  kind?: "expense" | "income",
): CategoryNode[] {
  const filtered = kind ? rows.filter((r) => r.kind === kind) : rows;
  const roots: CategoryNode[] = [];
  const byId = new Map<string, CategoryNode>();
  for (const row of filtered) {
    if (!row.parent_id) {
      const node = { ...row, children: [] };
      roots.push(node);
      byId.set(row.id, node);
    }
  }
  for (const row of filtered) {
    if (row.parent_id) {
      const parent = byId.get(row.parent_id);
      if (parent) {
        parent.children.push(row);
      } else {
        // Orphan (parent deleted): promoted to root so it is not lost.
        const node = { ...row, parent_id: null, children: [] };
        roots.push(node);
        byId.set(row.id, node);
      }
    }
  }
  return roots;
}

// ─── Transacciones ────────────────────────────────────────────────────────────

const toWallet = aliasedTable(wallet, "to_wallet");

export interface TransactionFilters {
  /** Rango contable [fromISO, toISO). */
  fromISO?: string;
  toISO?: string;
  walletId?: string;
  categoryId?: string;
  /** Texto contra note y payee. */
  text?: string;
  /** Partial (LIKE) match only against payee. */
  payee?: string;
  /** Inclusive amount range in minor units of the base currency. */
  minAmount?: number;
  maxAmount?: number;
  projectId?: string;
  limit?: number;
}

export function getTransactionsQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  filters: TransactionFilters = {},
) {
  const conditions = [eq(tx.user_id, userId), ...liveTxFilters()];
  if (filters.fromISO) {
    conditions.push(gte(tx.occurred_at, filters.fromISO));
  }
  if (filters.toISO) {
    conditions.push(lt(tx.occurred_at, filters.toISO));
  }
  if (filters.walletId) {
    const cond = or(eq(tx.wallet_id, filters.walletId), eq(tx.to_wallet_id, filters.walletId));
    if (cond) {
      conditions.push(cond);
    }
  }
  if (filters.categoryId) {
    conditions.push(eq(tx.category_id, filters.categoryId));
  }
  if (filters.text) {
    const pattern = `%${filters.text}%`;
    const cond = or(like(tx.note, pattern), like(tx.payee, pattern));
    if (cond) {
      conditions.push(cond);
    }
  }
  if (filters.payee) {
    conditions.push(like(tx.payee, `%${filters.payee}%`));
  }
  if (filters.minAmount !== undefined) {
    conditions.push(gte(tx.base_amount, filters.minAmount));
  }
  if (filters.maxAmount !== undefined) {
    conditions.push(lte(tx.base_amount, filters.maxAmount));
  }
  if (filters.projectId) {
    conditions.push(eq(tx.project_id, filters.projectId));
  }

  const query = db
    .select({
      id: tx.id,
      kind: tx.kind,
      amount: tx.amount,
      currency: tx.currency,
      base_amount: tx.base_amount,
      base_currency: tx.base_currency,
      exchange_rate_used: tx.exchange_rate_used,
      fee_amount: tx.fee_amount,
      wallet_id: tx.wallet_id,
      wallet_name: wallet.name,
      wallet_currency: wallet.currency,
      to_wallet_id: tx.to_wallet_id,
      to_amount: tx.to_amount,
      to_wallet_name: toWallet.name,
      category_id: tx.category_id,
      category_name: category.name,
      category_name_key: category.name_key,
      category_icon: category.icon,
      category_color: category.color,
      category_parent_id: category.parent_id,
      occurred_at: tx.occurred_at,
      note: tx.note,
      payee: tx.payee,
      project_id: tx.project_id,
      is_adjustment: tx.is_adjustment,
      cleared: tx.cleared,
    })
    .from(tx)
    .leftJoin(wallet, eq(tx.wallet_id, wallet.id))
    .leftJoin(toWallet, eq(tx.to_wallet_id, toWallet.id))
    .leftJoin(category, eq(tx.category_id, category.id))
    .where(and(...conditions))
    .orderBy(desc(tx.occurred_at), desc(tx.created_at));

  if (filters.limit !== undefined) {
    return query.limit(filters.limit);
  }
  return query;
}

export type TransactionWithMeta = Awaited<
  ReturnType<ReturnType<typeof getTransactionsQuery>["execute"]>
>[number];

/**
 * Period summary in base currency: expense, income, and balance.
 * Transfers do not count (move money, neither create nor destroy);
 * their fee is real expense.
 */
export function getPeriodSummaryQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  fromISO: string,
  toISO: string,
) {
  return db
    .select({
      expense: sql<number>`COALESCE(SUM(${expenseContributionSql()}), 0)`.as("expense"),
      income: sql<number>`COALESCE(SUM(${incomeContributionSql()}), 0)`.as("income"),
    })
    .from(tx)
    .where(
      and(
        eq(tx.user_id, userId),
        ...liveTxFilters(),
        gte(tx.occurred_at, fromISO),
        lt(tx.occurred_at, toISO),
      ),
    );
}

export interface PeriodSummaryRow {
  expense: number;
  income: number;
}

export function getTransactionDetailQuery(db: PowerSyncDrizzleDB, txId: string) {
  return db.select().from(tx).where(eq(tx.id, txId)).limit(1);
}

// ─── Trash (ACTIVITY-CORE L2) ───────────────────────────────────────────────────
// Views of deleted rows (deleted_at IS NOT NULL), the opposite of the live view.

/** Transactions in user trash (most recent first). */
export function getTrashedTransactionsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(tx)
    .where(and(eq(tx.user_id, userId), isNotNull(tx.deleted_at)))
    .orderBy(desc(tx.deleted_at));
}

/** Wallets in user trash. */
export function getTrashedWalletsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(wallet)
    .where(and(eq(wallet.user_id, userId), isNotNull(wallet.deleted_at)))
    .orderBy(desc(wallet.deleted_at));
}

/** Categories in user trash. */
export function getTrashedCategoriesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(category)
    .where(and(eq(category.user_id, userId), isNotNull(category.deleted_at)))
    .orderBy(desc(category.deleted_at));
}
